import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import Card from "../components/common/Card";
import Badge from "../components/common/Badge";
import { formatDate, formatDateISO } from "../utils/formatters";
import { barcodeService } from "../services/barcodeService";

const TaskDetailsPage = () => {
  const { serviceNo } = useParams();
  const location = useLocation();
  const fallbackName = new URLSearchParams(location.search).get("name") || location.state?.name;

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAvailable, setIsAvailable] = useState(null);
  const [fullName, setFullName] = useState(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!serviceNo) return;
    let mounted = true;
    const fetchFullName = async () => {
      try {
        const data = await barcodeService.getTaskDetailsRaw(serviceNo);
        const resultSet = data?.ResultSet;
        if (resultSet && resultSet.length > 0) {
          const first = resultSet[0];
          const incharge = String(first?.Incharge || "").trim();
          if (incharge && mounted) {
            const namePart = incharge.replace(/^\S+\s*-\s*/, "").trim();
            if (namePart) setFullName(namePart);
          }
        }
      } catch (e) {
        console.warn("Failed to fetch full name from API", e);
      }
    };
    fetchFullName();
    return () => { mounted = false; };
  }, [serviceNo]);

  const name = fullName || fallbackName;
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const set = await barcodeService.getTaskDetails(serviceNo);
      if (isMounted.current) setTasks(set || []);
    } catch (e) {
      console.error("Failed to load tasks", e);
      if (isMounted.current) setError("Failed to load tasks");
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [serviceNo]);

  useEffect(() => {
    loadTasks();
    const handlePop = () => loadTasks();
    const handlePageShow = (e) => { if (e.persisted) loadTasks(); };
    const handleVisibility = () => { if (document.visibilityState === "visible") loadTasks(); };
    window.addEventListener("popstate", handlePop);
    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("popstate", handlePop);
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loadTasks]);

  useEffect(() => {
    let mounted = true;
    const loadAvailability = async () => {
      if (!serviceNo) { setIsAvailable(null); return; }
      try {
        const result = await barcodeService.getAvailability(serviceNo);
        const available = String((result && result.Status) || "").toLowerCase().includes("available");
        if (mounted) setIsAvailable(available);
      } catch (e) {
        if (mounted) setIsAvailable(false);
      }
    };
    loadAvailability();
    return () => { mounted = false; };
  }, [serviceNo]);

  const getStatusColor = (status) => {
    const s = String(status || "").toLowerCase();
    if (!s) return "slate";
    if (s.includes("complete") || s.includes("done")) return "green";
    if (s.includes("progress") || s.includes("active") || s.includes("ongoing")) return "blue";
    if (s.includes("hold")) return "purple";
    if (s.includes("pending") || s.includes("queue")) return "yellow";
    if (s.includes("cancel") || s.includes("reject") || s.includes("fail")) return "red";
    return "slate";
  };

  const isCompletedStatus = (status) => {
    const s = String(status || "").toLowerCase();
    return s.includes("complete") || s.includes("done") || s.includes("finish");
  };

  const isInProgressStatus = (status) => {
    const s = String(status || "").toLowerCase();
    return s.includes("progress") || s.includes("active") || s.includes("ongoing");
  };

  const getRequestedByName = (value) => {
    const text = String(value || "").trim();
    if (!text) return "-";
    const parts = text.split(" - ");
    return parts.length > 1 ? parts.slice(1).join(" - ").trim() || "-" : text;
  };

  const getRequestedByServiceNo = (value) => {
    const text = String(value || "").trim();
    const match = text.match(/^(\d{4,})\s*-/);
    return match ? match[1] : null;
  };

  const getInitials = (fullName = "") => {
    const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "NA";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const getJobNumber = (task) => {
    const date = String(task?.IwdDate || "").trim();
    const ref = String(task?.ReferenceNo || "").trim();
    if (!date && !ref) return null;
    return `${date || "—"} / ${ref || "—"}`;
  };

  const visibleTasks = tasks.filter((t) => !isCompletedStatus(t.Status));
  const inProgressTasks = visibleTasks.filter((t) => isInProgressStatus(t.Status));
  const otherTasks = visibleTasks.filter((t) => !isInProgressStatus(t.Status));
  const profileImageUrl = serviceNo ? barcodeService.getUserImageUrl(serviceNo) : null;
  const profileLabel = name ? `${serviceNo} - ${name}` : serviceNo;

  /* ─── InProgress compact row - ALL TEXT matches name font size ───────────── */
  const renderInProgressRow = (t, i) => {
    const reqSvcNo = getRequestedByServiceNo(t.RequestedBy);
    const reqName  = getRequestedByName(t.RequestedBy);
    return (
      <div
        key={i}
        className="flex items-start gap-6 px-6 py-6 border-l-4 border-amber-500"
        style={{ background: "rgba(180,120,0,0.22)" }}
      >
        {/* Requester avatar - increased to w-28 h-28 (112px) */}
        <div className="relative w-28 h-28 rounded-full overflow-hidden bg-slate-600 shrink-0 mt-1">
          {reqSvcNo ? (
            <img
              src={barcodeService.getUserImageUrl(reqSvcNo)}
              alt={reqName}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="w-full h-full object-cover object-center"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-500 text-white text-3xl font-bold">
              {getInitials(reqName)}
            </div>
          )}
        </div>

        {/* Task info - ALL TEXT large like name */}
        <div className="flex-1 min-w-0">
          <div className="text-3xl md:text-4xl font-bold text-white line-clamp-2 mb-3">
            {t.Task || "Untitled task"}
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6">
            {/* Start Date - Green color for identification */}
            <div className="text-3xl md:text-4xl font-bold text-emerald-400 dark:text-emerald-300 tracking-tight">
              <span className="text-xl md:text-2xl font-semibold text-white">Start Date:</span>
              {" "}
              {formatDateISO(t.PlannedStartDate)}
            </div>
            {/* End Date - Red/Orange color for identification */}
            <div className="text-3xl md:text-4xl font-bold text-rose-400 dark:text-rose-300 tracking-tight">
              <span className="text-xl md:text-2xl font-semibold text-white">End Date:</span>
              {" "}
              {formatDateISO(t.PlannedCompletionDate)}
            </div>
          </div>
        </div>

        {/* Job Number + Hours - right side */}
        <div className="shrink-0 text-right flex flex-col items-end gap-2">
          {getJobNumber(t) && (
            <div className="flex flex-col items-end">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Job No.</span>
              <span className="text-lg md:text-xl font-bold text-amber-300 tabular-nums whitespace-nowrap bg-amber-500/20 px-3 py-1 rounded-md border border-amber-500/40">
                {getJobNumber(t)}
              </span>
            </div>
          )}
          <div>
            <div className="text-3xl md:text-4xl font-bold text-white tabular-nums">
              {`${t.HoursTaken || 0} / ${t.HoursAllocated || "-"}`}
            </div>
            <div className="text-base font-semibold text-slate-300 mt-1">Hours</div>
          </div>
        </div>
      </div>
    );
  };

  /* ─── Other Tasks full table row - with 2 line limit and scroll ─────────────────────────────────────── */
  const renderOtherRow = (t, i) => (
    <tr key={i} className="bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
      <td className="px-4 py-4 align-top text-center">
        <div className="flex justify-center">
          {/* Requester avatar - increased to w-20 h-20 */}
          <div className="relative w-20 h-20 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700 shrink-0">
            {getRequestedByServiceNo(t.RequestedBy) ? (
              <img
                src={barcodeService.getUserImageUrl(getRequestedByServiceNo(t.RequestedBy))}
                alt={getRequestedByName(t.RequestedBy)}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-400 dark:bg-slate-600 text-white text-2xl font-bold">
                {getInitials(getRequestedByName(t.RequestedBy))}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-4 align-top">
        {/* Task name with max 2 lines and scroll on hover */}
        <div className="group relative">
          <div className="font-bold text-3xl text-slate-800 dark:text-slate-100 leading-7 line-clamp-2">
            {t.Task || "Untitled task"}
          </div>
          {/* Scrollable tooltip on hover for full task details */}
          <div className="invisible group-hover:visible absolute z-50 left-0 top-full mt-2 w-96 max-h-60 overflow-y-auto bg-slate-900 text-white p-3 rounded-lg shadow-xl border border-slate-700 transition-all duration-200">
            <div className="text-sm whitespace-pre-wrap break-words">
              {t.Task || "Untitled task"}
            </div>
          </div>
        </div>
        <div className="mt-1 text-base">
          <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Start: {formatDateISO(t.PlannedStartDate)}</span>
          {" → "}
          <span className="text-rose-600 dark:text-rose-400 font-semibold">End: {formatDateISO(t.PlannedCompletionDate)}</span>
        </div>
      </td>
      <td className="px-4 py-4 align-top text-center text-slate-700 dark:text-slate-200 whitespace-nowrap tabular-nums font-bold text-2xl">
        {t.HoursAllocated || "-"}
      </td>
      <td className="px-4 py-4 align-top text-center text-slate-700 dark:text-slate-200 whitespace-nowrap tabular-nums font-bold text-2xl">
        {t.HoursTaken || 0}
      </td>
      <td className="px-4 py-4 align-top">
        {t.Status ? (
          <Badge
            label={t.Status}
            color={getStatusColor(t.Status)}
            className="whitespace-nowrap text-base px-3 py-1.5 rounded-md font-semibold"
          />
        ) : "-"}
      </td>
      <td className="px-4 py-4 align-top text-right">
        {getJobNumber(t) ? (
          <div className="flex flex-col items-end">
            <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Job No.</span>
            <span className="text-base md:text-lg font-bold text-amber-600 dark:text-amber-400 tabular-nums whitespace-nowrap bg-amber-50 dark:bg-amber-500/10 px-3 py-1 rounded-md border border-amber-200 dark:border-amber-500/30">
              {getJobNumber(t)}
            </span>
          </div>
        ) : "-"}
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      {/* ── Page header with name and service no side by side ────────────────── */}
      <div className="mb-2 flex justify-between items-end">
        <div>
          <p className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
            {name || serviceNo}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl md:text-3xl font-bold text-slate-600 dark:text-slate-300 tracking-wide">
            Service No: {serviceNo}
          </p>
        </div>
      </div>

      {/* ── Main Content: Profile Image + In Progress Tasks (full width) ────── */}
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        
        {/* Profile Image - left side - increased to 200px x 200px */}
        <Card
          padding={false}
          className="overflow-hidden bg-gradient-to-b from-white to-slate-50/60 dark:from-slate-900 dark:to-slate-900/70"
          style={{ width: "200px", flexShrink: 0 }}
        >
          <div className="relative" style={{ width: "200px", height: "200px" }}>
            {profileImageUrl && !imgError ? (
              <img
                src={profileImageUrl}
                alt={profileLabel}
                onError={() => setImgError(true)}
                style={{ width: "200px", height: "200px", objectFit: "contain", objectPosition: "center", display: "block" }}
              />
            ) : (
              <div
                className="flex items-center justify-center text-7xl font-bold text-slate-500 dark:text-slate-400"
                style={{ width: "200px", height: "200px" }}
              >
                {getInitials(name || serviceNo)}
              </div>
            )}
            {/* Availability dot - larger */}
            <span
              className={`absolute top-3 right-3 w-5 h-5 rounded-full ring-2 ring-white dark:ring-slate-900 ${
                isAvailable ? "bg-green-400" : "bg-slate-400"
              }`}
              title={isAvailable ? "Available" : "Not available"}
            />
          </div>
        </Card>

        {/* In Progress Tasks - takes remaining width, no old space */}
        <Card
          padding={false}
          className="flex flex-col overflow-hidden bg-gradient-to-b from-white to-slate-50/60 dark:from-slate-900 dark:to-slate-900/70 flex-1"
        >
          {/* Rows */}
          <div
            className="overflow-y-auto divide-y divide-slate-700/30 flex-1"
            style={{ maxHeight: "600px" }}
          >
            {loading ? (
              <div className="px-5 py-8 text-center text-xl text-slate-400">Loading tasks…</div>
            ) : error ? (
              <div className="px-5 py-8 text-center text-xl text-red-400">{error}</div>
            ) : inProgressTasks.length === 0 ? (
              <div className="px-5 py-8 text-center text-xl text-slate-400">No in-progress tasks.</div>
            ) : (
              inProgressTasks.map((t, i) => renderInProgressRow(t, i))
            )}
          </div>
        </Card>
      </div>

      {/* ── BOTTOM: Other Tasks with horizontal scroll for table ─────────────────────────────────────────── */}
      <Card padding={false} className="w-full overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-xl md:text-2xl font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Other Tasks
          </h3>
        </div>

        {/* Horizontal scroll container for the table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-4 py-6 text-center text-base text-slate-400">Loading tasks…</div>
          ) : otherTasks.length === 0 ? (
            <div className="m-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 px-4 py-6 text-center text-base text-slate-400">
              No other tasks.
            </div>
          ) : (
            <table className="min-w-[800px] md:min-w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 text-sm text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3 text-center whitespace-nowrap font-bold">Requested By</th>
                  <th className="px-4 py-3 text-left font-bold">Task</th>
                  <th className="px-4 py-3 text-center whitespace-nowrap font-bold">Allocated Hrs</th>
                  <th className="px-4 py-3 text-center whitespace-nowrap font-bold">Actual Hrs</th>
                  <th className="px-4 py-3 text-center font-bold">Status</th>
                  <th className="px-4 py-3 text-right whitespace-nowrap font-bold">Job No.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {otherTasks.map((t, i) => renderOtherRow(t, i))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TaskDetailsPage;